import clsx from 'clsx';
import { ChangeEvent, useCallback, useMemo, useRef } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useContent, useFormattedContent } from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { PosCurrencyField } from 'src/core/POS/PosCurrencyField';
import { PosFormField } from 'src/core/POS/PosFormField';
import {
  getTextFieldState,
  PosTextFieldProps,
} from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import {
  CheckIcon,
  CrossIcon,
  IconsFill,
  ProcessingIcon,
} from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { FLIGHT_DURATION_TO_CID } from 'src/utils/constants/contentIdMaps';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { posChangedField, posField } from 'src/utils/posFieldUtils';
import {
  FlightDuration,
  SponsoredListingsCampaignInput,
} from 'src/WebApiController';

import { useCampaignRowFormContext } from '../CampaignRowForm';
import * as styles from './BaseBidInput.css';

type BaseBidInputProps<T> = {
  currencyCode: string;
  rootClassName?: string;
} & Omit<
  PosTextFieldProps,
  'type' | 'errors' | 'currency' | 'value' | 'onChange'
>;

export const BaseBidInput = <T extends FieldValues>({
  currencyCode,
  disabled,
  placeholder,
  rootClassName,
  ...rest
}: BaseBidInputProps<T>) => {
  const { getUiCurrency } = useLocalizationContext();
  const {
    setValue,
    watch,
    setError,
    clearErrors,
    getFieldState,
    formState,
    handleSubmit,
  } = useFormContext<SponsoredListingsCampaignInput>();

  const { onSubmit, isLoading } = useCampaignRowFormContext();

  const baseBid = watch('baseBid');
  const currentFlightBudget = watch('currentFlightBudget');
  const recurringFlightBudget = watch('recurringFlightBudget');
  const flightDuration = watch('flightDuration');

  const uiCurrency = useMemo(
    () => getUiCurrency(currencyCode),
    [currencyCode, getUiCurrency]
  );

  const requiredMsg = useContent(ContentId.Required);
  const flightDurationText = useContent(
    FLIGHT_DURATION_TO_CID[flightDuration.value ?? FlightDuration.Daily]
  );
  const baseBidExceedsBudgetText = useFormattedContent(
    FormatContentId.BaseBidExceedsBudgetError,
    [`${flightDurationText}`]
  );

  const fieldError = getFieldState('baseBid', formState)?.error?.message;

  const onChangeHandler = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = parseFloat(e.target.value) ?? 0;

      setValue('baseBid', posChangedField(newValue));
    },
    [setValue]
  );

  const onBlurHandler = useCallback(
    (e: React.FocusEvent<HTMLInputElement, Element>) => {
      clearErrors('baseBid');
      if (baseBid.value == null) {
        setError('baseBid', { message: requiredMsg });
      } else if (
        (currentFlightBudget.value &&
          baseBid.value >= currentFlightBudget.value) ||
        (recurringFlightBudget.value &&
          baseBid.value >= recurringFlightBudget.value)
      ) {
        setError('baseBid', {
          type: 'manual',
          message: baseBidExceedsBudgetText as string,
        });
      }
    },
    [
      baseBid.value,
      baseBidExceedsBudgetText,
      clearErrors,
      currentFlightBudget.value,
      recurringFlightBudget.value,
      requiredMsg,
      setError,
    ]
  );

  const saveMsg = useContent(ContentId.Save);
  const cancelMsg = useContent(ContentId.Cancel);

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <PosFormField
      errors={disabled ? undefined : fieldError}
      showErrorsInline
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <PosCurrencyField
        rootProps={{
          state: getTextFieldState(fieldError),
          disabled: disabled,
          className: clsx(rootClassName, styles.textFieldOverride),
          shape: 'borderless',
          variant: 'empty',
        }}
        placeholder={placeholder}
        alignment="right"
        uiCurrency={uiCurrency}
        value={baseBid?.value ?? undefined}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onChange={onChangeHandler}
        onBlur={onBlurHandler}
        onKeyUp={(e) => {
          if (e.key === 'Enter') {
            e.stopPropagation();
            handleSubmit(onSubmit)();
          }
        }}
        disabled={disabled}
        ref={inputRef}
        {...rest}
        postfixDisplay={
          isLoading ? (
            <div className="operation-button">
              <RotatingWrapper>
                <ProcessingIcon size={vars.iconSize.l} />
              </RotatingWrapper>
            </div>
          ) : !disabled && !fieldError && baseBid.hasChanged ? (
            <>
              <CheckIcon
                title={saveMsg}
                withHoverEffect
                size={vars.iconSize.s}
                fill={IconsFill.textSuccess}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSubmit(onSubmit)();
                }}
              />
              <CrossIcon
                title={cancelMsg}
                withHoverEffect
                size={vars.iconSize.s}
                fill={IconsFill.textDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  setValue(
                    'baseBid',
                    posField(
                      formState.defaultValues?.['baseBid']?.value ?? null
                    )
                  );
                  clearErrors('baseBid');
                  inputRef.current?.blur();
                }}
              />
            </>
          ) : undefined
        }
      />
    </PosFormField>
  );
};
